import { Checkbox } from '@/libs/components/src/lib/form/Checkbox';
import { Input } from '@chiroup/components/form/Input';
import { QuestionAnswer } from '@chiroup/core/types/ComplaintSummary.type';
import React, { useState } from 'react';

interface CheckboxWithInputsProps {
  questionId: string;
  questionData: QuestionAnswer;
  onAnswerChange: (
    questionId: string,
    newValue: string | string[],
    otherValue?: string,
    customInputData?: Record<string, string>
  ) => void;
}

const CheckboxWithInputs: React.FC<CheckboxWithInputsProps> = ({
  questionId,
  questionData,
  onAnswerChange,
}) => {
  const { options = [], selectedValues = [], otherValue, customInputData = {} } = questionData;

  // Handle both customInputData and data field formats (data might be used in some cases)
  const initialData = (questionData as any).data || customInputData || {};

  // Local state to manage custom input data
  const [localCustomInputData, setLocalCustomInputData] = useState(initialData);

  // Check if any option has id 'other' to enable the includeOther prop
  const hasOtherOption = options.some(opt => opt.id === 'other');

  // Filter out the 'other' option from regular options since we handle it separately
  const regularOptions = options.filter(opt => opt.id !== 'other');

  const hasSelectedOther = selectedValues.includes('other');

  const handleOptionChange = (optionId: string, isChecked: boolean) => {
    let newSelectedValues: string[];

    if (isChecked) {
      newSelectedValues = [...selectedValues, optionId];
    } else {
      newSelectedValues = selectedValues.filter(v => v !== optionId);

      // Remove custom input data for this option if unchecked
      if (localCustomInputData[optionId]) {
        const newCustomInputData = { ...localCustomInputData };
        delete newCustomInputData[optionId];
        setLocalCustomInputData(newCustomInputData);
        // Pass the updated custom input data
        onAnswerChange(questionId, newSelectedValues, otherValue, newCustomInputData);

        return;
      }
    }

    onAnswerChange(questionId, newSelectedValues, otherValue, localCustomInputData);
  };

  const handleCustomInputChange = (optionId: string, value: string) => {
    const newCustomInputData = { ...localCustomInputData, [optionId]: value };
    setLocalCustomInputData(newCustomInputData);

    // Pass the updated custom input data to the parent
    onAnswerChange(questionId, selectedValues, otherValue, newCustomInputData);
  };

  return (
    <div className="max-w-xl space-y-2">
      {regularOptions.map(option => {
        const isSelected = selectedValues.includes(option.id);

        return (
          <div key={option.id} className="flex items-center gap-3">
            <Checkbox
              label={option.display}
              value={isSelected}
              onChange={value => handleOptionChange(option.id, value)}
            />
            {isSelected && (
              <Input
                name={`edit-${questionId}-${option.id}`}
                type="text"
                value={localCustomInputData[option.id] || ''}
                onChange={value => handleCustomInputChange(option.id, value)}
                placeholder="Name of provider"
                className="w-56 primary"
              />
            )}
          </div>
        );
      })}

      {hasOtherOption && (
        <div className="flex items-center gap-3">
          <Checkbox
            label="Other"
            value={hasSelectedOther}
            onChange={value =>
              onAnswerChange(
                questionId,
                value ? selectedValues.concat('other') : selectedValues.filter(v => v !== 'other'),
                otherValue,
                localCustomInputData
              )
            }
          />
          {hasSelectedOther && (
            <Input
              name={`edit-${questionId}-other`}
              type="text"
              value={otherValue || ''}
              onChange={value =>
                onAnswerChange(questionId, selectedValues, value, localCustomInputData)
              }
              placeholder="Other provider"
              className="w-56 primary"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default CheckboxWithInputs;
